import { useMutation, useQuery } from '@apollo/client/react';
import {
  Check,
  EllipsisVerticalIcon,
  MoveDiagonalIcon,
  PanelRightClose,
  PanelRightOpen,
  Pen,
  Plus,
  TrashIcon
} from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CreateCommitteeForm } from '@/components/committees/CreateCommitteeForm';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/Dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { SearchBar } from '@/components/ui/search-bar';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { useOdooAuth } from '@/contexts/OdooAuthContext';
import {
  type AuditLog,
  DELETE_COMMITTEE,
  GET_ACTIVE_FUNDS,
  GET_COMMITTEES,
  type GetActiveFundsData,
  type GetCommitteesData,
  type MemberAuditLog,
  REMOVE_COMMITTEE_MEMBER,
  SYNC_COMMITTEE_MEMBERS,
  UPDATE_COMMITTEE
} from '@/lib/graphql/committees';
import { GET_ALL_USERS, type GetAllUsersData } from '@/lib/graphql/users';
import { cn } from '@/lib/utils';
import { formatRelativeTime } from '@/utils/date';
import { getInitials } from '@/utils/getInitials';

const DeleteCommitteeDialog = memo(function DeleteCommitteeDialog({
  committee,
  onOpenChange,
  open
}: {
  committee: { id: string; name: string } | null;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}) {
  const [deleteCommittee, { loading }] = useMutation(DELETE_COMMITTEE, {
    refetchQueries: [{ query: GET_COMMITTEES }]
  });

  const handleDelete = useCallback(() => {
    if (!committee) return;

    deleteCommittee({
      variables: {
        id: committee.id
      }
    })
      .then(() => {
        onOpenChange(false);
      })
      .catch((error: unknown) => {
        console.error('Failed to delete committee:', error);
      });
  }, [committee, deleteCommittee, onOpenChange]);

  return (
    <Dialog
      onOpenChange={onOpenChange}
      open={open}
    >
      <DialogContent
        aria-describedby={undefined}
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>Delete Committee</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className="text-sm text-gray-600">
            Are you sure you want to delete{' '}
            <span className="font-medium">{committee?.name}</span>? This action
            cannot be undone.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button
            onClick={() => {
              onOpenChange(false);
            }}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            className="bg-red-600 hover:bg-red-700"
            disabled={loading}
            onClick={handleDelete}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

const CommitteesTable = memo(function CommitteesTable({
  committees,
  onExpand,
  searchTerm
}: {
  committees: GetCommitteesData['committees'];
  onExpand: (committee: GetCommitteesData['committees'][number]) => void;
  searchTerm?: string;
}) {
  const [committeeToDelete, setCommitteeToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  if (committees.length === 0) {
    const isSearch = Boolean(searchTerm?.trim());
    return (
      <div className="px-14 py-6">
        <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
          <div className="flex flex-col items-center gap-2 text-center">
            <h3 className="text-xl font-semibold">
              {isSearch
                ? 'No committees match your search'
                : 'No committees set up yet'}
            </h3>
            <p className="text-muted-foreground max-w-md text-sm">
              {isSearch
                ? 'Try a different search term or clear the search to see all committees.'
                : 'No committee has been set up yet. Create a committee to get started.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="px-14 py-6">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-0" />
              <TableHead className="max-w-[250px] text-xs font-semibold">
                Committee Name
              </TableHead>
              <TableHead className="max-w-[200px] text-xs font-semibold">
                Fund
              </TableHead>
              <TableHead className="text-xs font-semibold">
                Threshold Requirement
              </TableHead>
              <TableHead className="text-xs font-semibold">
                Member Count
              </TableHead>
              <TableHead className="w-0" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {committees.map(committee => (
              <TableRow key={committee.id}>
                <TableCell className="w-0">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={() => {
                          onExpand(committee);
                        }}
                        size="icon"
                        variant="ghost"
                      >
                        <MoveDiagonalIcon />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Committee Details</TooltipContent>
                  </Tooltip>
                </TableCell>
                <TableCell className="max-w-[250px] overflow-hidden font-medium">
                  {committee.name && committee.name.length > 50 ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="block cursor-default truncate">
                          {committee.name}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        {committee.name}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <span className="block truncate">
                      {committee.name || 'Unnamed Committee'}
                    </span>
                  )}
                </TableCell>
                <TableCell className="max-w-[200px] overflow-hidden">
                  {committee.fundId?.name &&
                  committee.fundId.name.length > 50 ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="block cursor-default truncate">
                          {committee.fundId.name}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        {committee.fundId.name}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <span className="block truncate">
                      {committee.fundId?.name || '-'}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {committee.threshold}
                </TableCell>
                <TableCell className="text-right">
                  {committee.memberships.length}
                </TableCell>
                <TableCell className="w-0">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="icon-sm"
                        variant="ghost"
                      >
                        <EllipsisVerticalIcon />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => {
                          setCommitteeToDelete({
                            id: committee.id,
                            name: committee.name
                          });
                        }}
                        variant="destructive"
                      >
                        <TrashIcon />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <DeleteCommitteeDialog
        committee={committeeToDelete}
        onOpenChange={open => {
          if (!open) {
            setCommitteeToDelete(null);
          }
        }}
        open={committeeToDelete !== null}
      />
    </>
  );
});

const CommitteeDetailsMeta = memo(function CommitteeDetailsMeta({
  committee
}: {
  committee: GetCommitteesData['committees'][number];
}) {
  const { role } = useOdooAuth();
  const isAdmin = role === 'admin';
  const [isEditing, setIsEditing] = useState(false);
  const [fundDropdownOpen, setFundDropdownOpen] = useState(false);
  const [fundSearch, setFundSearch] = useState('');
  const [editFields, setEditFields] = useState({
    committeeName: committee.name,
    fundId: committee.fundId?.id ?? (null as null | string),
    threshold: committee.threshold.toString()
  });

  const { data: fundsData } = useQuery<GetActiveFundsData>(GET_ACTIVE_FUNDS);
  const [updateCommittee, { loading: updateLoading }] = useMutation(
    UPDATE_COMMITTEE,
    {
      refetchQueries: [{ query: GET_COMMITTEES }]
    }
  );

  const filteredFunds = useMemo(() => {
    const funds = fundsData?.funds.edges.map(e => e.node) ?? [];
    if (!fundSearch) return funds;
    return funds.filter(f =>
      f.name.toLowerCase().includes(fundSearch.toLowerCase())
    );
  }, [fundsData?.funds.edges, fundSearch]);

  const selectedFundName = useMemo(() => {
    if (!editFields.fundId) return null;
    const fund = fundsData?.funds.edges.find(
      e => e.node.id === editFields.fundId
    );
    return fund?.node.name ?? committee.fundId?.name ?? null;
  }, [editFields.fundId, fundsData?.funds.edges, committee.fundId?.name]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setEditFields({
      committeeName: committee.name,
      fundId: committee.fundId?.id ?? null,
      threshold: committee.threshold.toString()
    });
  }, [committee.fundId?.id, committee.name, committee.threshold]);

  const handleSave = useCallback(() => {
    updateCommittee({
      variables: {
        id: committee.id,
        input: {
          fundId: editFields.fundId,
          name: editFields.committeeName,
          threshold: parseInt(editFields.threshold, 10)
        }
      }
    })
      .then(() => {
        setIsEditing(false);
      })
      .catch((error: unknown) => {
        console.error('Failed to update committee:', error);
      });
  }, [updateCommittee, committee.id, editFields]);

  const handleFundSelect = useCallback((fundId: string) => {
    setEditFields(prev => ({ ...prev, fundId }));
    setFundDropdownOpen(false);
    setFundSearch('');
  }, []);

  return (
    <>
      <div className="mb-2.5 flex w-full flex-row items-center justify-between">
        <div className="w-fit text-lg font-[600]">Commitee Details</div>
        {isEditing ? (
          <div className="flex gap-2">
            <Button
              onClick={handleCancel}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              className="bg-[#003455] hover:bg-[#003455]/90"
              disabled={updateLoading}
              onClick={handleSave}
            >
              {updateLoading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        ) : (
          isAdmin && (
            <Button
              className="text-dark-blue-primary-smc-500"
              onClick={() => {
                setIsEditing(true);
              }}
              variant={'ghost'}
            >
              <Pen
                height={16}
                width={16}
              />
              Edit
            </Button>
          )
        )}
      </div>
      <div className="space-y-5 rounded-md border-1 border-[#E5E5E5] p-5 text-sm">
        <div className="flex flex-row items-center">
          <div className="w-80">
            Committee Name <span className="text-red-500">*</span>
          </div>
          {isEditing ? (
            <Input
              className="w-64"
              onChange={e => {
                setEditFields(prev => ({
                  ...prev,
                  committeeName: e.target.value
                }));
              }}
              placeholder="Enter committee name"
              value={editFields.committeeName}
            />
          ) : (
            <div>{editFields.committeeName || 'N/A'}</div>
          )}
        </div>
        <div>
          <Label className="mb-1">Description</Label>
          <div className="flex-1">
            <Textarea
              defaultValue={committee.description ?? 'N/A'}
              disabled={!isEditing}
              placeholder="Enter description"
            />
          </div>
        </div>
        <div className="flex flex-row gap-12">
          <div className="flex flex-row items-center gap-2.5">
            <div className="w-80">Fund Linkage</div>
            {isEditing ? (
              <Popover
                onOpenChange={open => {
                  setFundDropdownOpen(open);
                  if (!open) setFundSearch('');
                }}
                open={fundDropdownOpen}
              >
                <PopoverTrigger asChild>
                  <Badge
                    className="cursor-pointer font-[600]"
                    variant="pastel-slate"
                  >
                    {selectedFundName ?? 'N/A'}
                  </Badge>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="w-64 p-2"
                >
                  <SearchBar
                    className="mb-2"
                    onChange={e => {
                      setFundSearch(e.target.value);
                    }}
                    placeholder="Search funds..."
                    value={fundSearch}
                  />
                  <div className="max-h-48 overflow-y-auto">
                    {filteredFunds.length === 0 ? (
                      <div className="py-2 text-center text-sm text-gray-500">
                        No funds found
                      </div>
                    ) : (
                      filteredFunds.map(fund => (
                        <div
                          className="flex cursor-pointer items-center justify-between rounded px-2 py-1.5 hover:bg-gray-100"
                          key={fund.id}
                          onClick={() => {
                            handleFundSelect(fund.id);
                          }}
                        >
                          <span className="text-xs">{fund.name}</span>
                          {editFields.fundId === fund.id && (
                            <Check className="h-4 w-4 text-green-600" />
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            ) : (
              <Badge
                className="font-[600]"
                variant="pastel-slate"
              >
                {selectedFundName ?? 'N/A'}
              </Badge>
            )}
          </div>
          <div className="flex flex-row items-center gap-2.5">
            <div className="w-80">Threshold</div>
            {isEditing ? (
              <Input
                className="w-24"
                onChange={e => {
                  setEditFields(prev => ({
                    ...prev,
                    threshold: e.target.value
                  }));
                }}
                placeholder="0"
                type="number"
                value={editFields.threshold}
              />
            ) : (
              <div>{editFields.threshold || 'N/A'}</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
});

const AddMemberDialog = ({
  committeeId,
  existingMemberIds,
  onOpenChange,
  open,
  removeUserIdRef,
  threshold
}: {
  committeeId: string;
  existingMemberIds: Set<string>;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  removeUserIdRef?: { current: ((userId: string) => void) | null };
  threshold: number;
}) => {
  const [memberSearch, setMemberSearch] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(
    () => new Set(existingMemberIds)
  );

  const { data: usersData } = useQuery<GetAllUsersData>(GET_ALL_USERS);
  const [syncMembers, { loading: syncLoading }] = useMutation(
    SYNC_COMMITTEE_MEMBERS,
    {
      refetchQueries: [{ query: GET_COMMITTEES }]
    }
  );

  const filteredUsers = useMemo(() => {
    const users = usersData?.users.edges.map(e => e.node) ?? [];
    if (!memberSearch) return users;
    return users.filter(u =>
      u.name.toLowerCase().includes(memberSearch.toLowerCase())
    );
  }, [usersData?.users.edges, memberSearch]);

  const toggleUser = useCallback((userId: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }, []);

  const removeUserId = useCallback((userId: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      next.delete(userId);
      return next;
    });
  }, []);

  useEffect(() => {
    if (removeUserIdRef) {
      removeUserIdRef.current = removeUserId;
    }
  }, [removeUserId, removeUserIdRef]);

  const handleAddMembers = useCallback(() => {
    const members = Array.from(selectedUserIds).map(userId => ({
      role: 'member',
      userId
    }));

    syncMembers({
      variables: {
        committeeId,
        members
      }
    })
      .then(() => {
        onOpenChange(false);
      })
      .catch((error: unknown) => {
        console.error('Failed to sync committee members:', error);
      });
  }, [selectedUserIds, syncMembers, committeeId, onOpenChange]);

  const selectedUsersHasChanged = useMemo(() => {
    return (
      !Array.from(selectedUserIds).every(id => existingMemberIds.has(id)) ||
      !Array.from(existingMemberIds).every(id => selectedUserIds.has(id))
    );
  }, [selectedUserIds, existingMemberIds]);

  const minMemberThreshold = useMemo(() => Math.max(0, threshold), [threshold]);
  const isBelowMemberThreshold = useMemo(() => {
    if (minMemberThreshold <= 0) return false;
    return selectedUserIds.size < minMemberThreshold;
  }, [minMemberThreshold, selectedUserIds]);
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (newOpen) {
        setSelectedUserIds(new Set(existingMemberIds));
        setMemberSearch('');
      }
      onOpenChange(newOpen);
    },
    [existingMemberIds, onOpenChange]
  );

  return (
    <Dialog
      onOpenChange={handleOpenChange}
      open={open}
    >
      <DialogContent
        aria-describedby={undefined}
        className="sm:max-w-3xl"
      >
        <DialogHeader>
          <DialogTitle>Manage Members</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <SearchBar
            onChange={e => {
              setMemberSearch(e.target.value);
            }}
            placeholder="Search for members to add"
            value={memberSearch}
          />
          <div className="max-h-72 overflow-y-auto">
            {filteredUsers.map(user => (
              <label
                className="flex cursor-pointer items-center justify-between px-2 py-3 hover:bg-gray-50"
                key={user.id}
              >
                <div className="flex items-center gap-3">
                  <Avatar>
                    {user.partner?.image128 && (
                      <AvatarImage
                        alt={user.name}
                        src={
                          user.partner.image128.startsWith('data:')
                            ? user.partner.image128
                            : `data:image/png;base64,${user.partner.image128}`
                        }
                      />
                    )}
                    <AvatarFallback>{getInitials(user.name)}</AvatarFallback>
                  </Avatar>
                  <span className="text-xs">{user.name}</span>
                </div>
                <Checkbox
                  checked={selectedUserIds.has(user.id)}
                  onCheckedChange={() => {
                    toggleUser(user.id);
                  }}
                />
              </label>
            ))}
          </div>
        </DialogBody>
        <DialogFooter className="flex items-center justify-end">
          {isBelowMemberThreshold ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    className="bg-[#003455] hover:bg-[#003455]/90"
                    disabled={
                      syncLoading ||
                      !selectedUsersHasChanged ||
                      isBelowMemberThreshold
                    }
                    onClick={handleAddMembers}
                  >
                    {syncLoading ? 'Saving...' : 'Save'}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                Selected members cannot be below {minMemberThreshold} threshold
              </TooltipContent>
            </Tooltip>
          ) : (
            <Button
              className="bg-[#003455] hover:bg-[#003455]/90"
              disabled={syncLoading || !selectedUsersHasChanged}
              onClick={handleAddMembers}
            >
              {syncLoading ? 'Saving...' : 'Save'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DeleteMemberDialog = memo(function DeleteMemberDialog({
  member,
  onMemberRemoved,
  onOpenChange,
  open
}: {
  member: { membershipId: string; name: string; userId?: string } | null;
  onMemberRemoved?: (userId: string) => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}) {
  const [removeMember, { loading }] = useMutation(REMOVE_COMMITTEE_MEMBER, {
    refetchQueries: [{ query: GET_COMMITTEES }]
  });

  const handleDelete = useCallback(() => {
    if (!member) return;

    removeMember({
      variables: {
        membershipId: member.membershipId
      }
    })
      .then(() => {
        if (member.userId && onMemberRemoved) {
          onMemberRemoved(member.userId);
        }
        onOpenChange(false);
      })
      .catch((error: unknown) => {
        console.error('Failed to remove committee member:', error);
      });
  }, [member, removeMember, onOpenChange, onMemberRemoved]);

  return (
    <Dialog
      onOpenChange={onOpenChange}
      open={open}
    >
      <DialogContent
        aria-describedby={undefined}
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>Remove Member</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className="text-sm text-gray-600">
            Are you sure you want to remove{' '}
            <span className="font-medium">{member?.name}</span> from this
            committee?
          </p>
        </DialogBody>
        <DialogFooter>
          <Button
            onClick={() => {
              onOpenChange(false);
            }}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            className="bg-red-600 hover:bg-red-700"
            disabled={loading}
            onClick={handleDelete}
          >
            {loading ? 'Removing...' : 'Remove'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

const CreateCommitteeDialog = ({
  onOpenChange,
  open
}: {
  onOpenChange: (open: boolean) => void;
  open: boolean;
}) => {
  return (
    <Dialog
      onOpenChange={onOpenChange}
      open={open}
    >
      <DialogContent
        aria-describedby={undefined}
        className="flex max-h-[calc(100%-40px)] w-[840px] max-w-[calc(100%-40px)] flex-col p-0 outline-none sm:max-w-[calc(100%-40px)]"
      >
        <DialogBody>
          <CreateCommitteeForm
            onClose={() => {
              onOpenChange(false);
            }}
          />
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

const MembersSection = memo(function MembersSection({
  committee
}: {
  committee: GetCommitteesData['committees'][number];
}) {
  const { role } = useOdooAuth();
  const isAdmin = role === 'admin';
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<{
    membershipId: string;
    name: string;
    userId?: string;
  } | null>(null);
  const addMemberDialogRemoveUserIdRef = useRef<
    ((userId: string) => void) | null
  >(null);

  const existingMemberIds = useMemo(
    () =>
      new Set(
        committee.memberships
          .map(m => m.userId?.id)
          .filter((id): id is string => Boolean(id))
      ),
    [committee.memberships]
  );
  return (
    <>
      <div className="mb-2.5 flex w-full flex-row items-center justify-between">
        <div className="w-fit text-lg font-[600]">Members Section</div>
        {committee.memberships.length > 0 && isAdmin && (
          <Button
            className="h-9 bg-[#003455] hover:bg-[#003455]/90"
            onClick={() => {
              setIsAddMemberOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Manage Members
          </Button>
        )}
      </div>
      {committee.memberships.length === 0 ? (
        <div className="flex items-center justify-center rounded-md border-1 border-[#E5E5E5] bg-white p-12">
          <div className="flex flex-col items-center gap-3">
            <div className="text-base font-semibold">No Members Yet</div>
            <div className="text-sm text-gray-500">
              Add members to your committee
            </div>
            {isAdmin && (
              <Button
                className="mt-2 h-9 bg-[#003455] hover:bg-[#003455]/90"
                onClick={() => {
                  setIsAddMemberOpen(true);
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Manage Members
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-5 rounded-md border-1 border-[#E5E5E5] p-5 text-sm">
          <div className="mb-3 text-sm font-medium">Committee Name</div>
          <Table>
            <TableBody>
              {committee.memberships.map(membership => (
                <TableRow key={membership.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar>
                        {membership.userId?.partner?.image128 && (
                          <AvatarImage
                            alt={membership.userId.name}
                            src={
                              membership.userId.partner.image128.startsWith(
                                'data:'
                              )
                                ? membership.userId.partner.image128
                                : `data:image/png;base64,${membership.userId.partner.image128}`
                            }
                          />
                        )}
                        <AvatarFallback>
                          {membership.userId
                            ? getInitials(membership.userId.name)
                            : '--'}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-xs">
                        {membership.userId?.name ?? 'N/A'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    {isAdmin && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="icon-sm"
                            variant="ghost"
                          >
                            <EllipsisVerticalIcon />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              if (membership.userId) {
                                setMemberToDelete({
                                  membershipId: membership.id,
                                  name: membership.userId.name,
                                  userId: membership.userId.id
                                });
                              }
                            }}
                            variant="destructive"
                          >
                            <TrashIcon />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AddMemberDialog
        committeeId={committee.id}
        existingMemberIds={existingMemberIds}
        onOpenChange={setIsAddMemberOpen}
        open={isAddMemberOpen}
        removeUserIdRef={addMemberDialogRemoveUserIdRef}
        threshold={committee.threshold}
      />

      <DeleteMemberDialog
        member={memberToDelete}
        onMemberRemoved={userId => {
          if (addMemberDialogRemoveUserIdRef.current) {
            addMemberDialogRemoveUserIdRef.current(userId);
          }
        }}
        onOpenChange={open => {
          if (!open) {
            setMemberToDelete(null);
          }
        }}
        open={memberToDelete !== null}
      />
    </>
  );
});

const AuditSection = memo(function AuditSection({
  auditLogs,
  memberAuditLogs
}: {
  auditLogs: AuditLog[];
  memberAuditLogs: MemberAuditLog[];
}) {
  const combinedLogs = useMemo(() => {
    const filteredAuditLogs = auditLogs.filter(
      log => log.trackingValues.length > 0
    );

    return [...filteredAuditLogs, ...memberAuditLogs].sort((a, b) => {
      const aDate =
        'createDate' in a && a.createDate
          ? a.createDate
          : (a.trackingValues[0]?.createDate ?? null);
      const bDate =
        'createDate' in b && b.createDate
          ? b.createDate
          : (b.trackingValues[0]?.createDate ?? null);
      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;
      return (
        new Date(bDate as string).getTime() -
        new Date(aDate as string).getTime()
      );
    });
  }, [auditLogs, memberAuditLogs]);

  const userAvatar = (name: string) => (
    <div className="flex items-center gap-2">
      <Avatar className="size-6">
        <AvatarFallback className="bg-blue-200 text-sm text-blue-700">
          {getInitials(name)}
        </AvatarFallback>
      </Avatar>
      <span className="text-sm font-medium">{name}</span>
    </div>
  );

  const renderAuditLogs = (log: AuditLog | MemberAuditLog) => {
    const keysMapping: Record<string, string> = {
      description: 'Description',
      fund_id: 'Fund linkage',
      name: 'Committee name',
      threshold: 'Threshold'
    };
    const ismemberAuditLog = 'membershipRecord' in log;
    const hasTrackingValues = log.trackingValues.length > 0;
    const isMemberAdded = ismemberAuditLog && !hasTrackingValues;
    const isMemberRemoved =
      ismemberAuditLog &&
      hasTrackingValues &&
      log.trackingValues[0]?.fieldName === 'end_date' &&
      log.trackingValues[0]?.newValue !== 'False';
    const isMemberReAdded =
      ismemberAuditLog &&
      hasTrackingValues &&
      log.trackingValues[0]?.fieldName === 'end_date' &&
      log.trackingValues[0]?.newValue === 'False';

    const timestamp =
      ('createDate' in log && log.createDate) ??
      log.trackingValues[0]?.createDate ??
      null;
    const relativeTime = formatRelativeTime(timestamp as string);

    if (isMemberRemoved) {
      return (
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2">
            {userAvatar(log.authorName)}
            <span className="text-xs">Removed</span>
            {userAvatar(log.membershipRecord?.userId?.name ?? '')}
          </div>
          {relativeTime && (
            <span className="shrink-0 text-xs text-gray-500">
              {relativeTime}
            </span>
          )}
        </div>
      );
    } else if (isMemberReAdded) {
      return (
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2">
            {userAvatar(log.authorName)}
            <span className="text-xs">Re-added</span>
            {userAvatar(log.membershipRecord?.userId?.name ?? '')}
          </div>
          {relativeTime && (
            <span className="shrink-0 text-xs text-gray-500">
              {relativeTime}
            </span>
          )}
        </div>
      );
    } else if (isMemberAdded) {
      return (
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2">
            {userAvatar(log.authorName)}
            <span className="text-xs">Added</span>
            {userAvatar(log.membershipRecord?.userId?.name ?? '')}
          </div>
          {relativeTime && (
            <span className="shrink-0 text-xs text-gray-500">
              {relativeTime}
            </span>
          )}
        </div>
      );
    } else if (
      hasTrackingValues &&
      Object.keys(keysMapping).includes(log.trackingValues[0]?.fieldName ?? '')
    ) {
      return (
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2">
            {userAvatar(log.authorName)}
            <span className="text-xs">Edited</span>
            <Badge variant="pastel-slate">
              {keysMapping[log.trackingValues[0]?.fieldName]}
            </Badge>
          </div>
          {relativeTime && (
            <span className="shrink-0 text-xs text-gray-500">
              {relativeTime}
            </span>
          )}
        </div>
      );
    }
    return <></>;
  };
  return (
    <div className="space-y-3">
      {combinedLogs.length === 0 ? (
        <div className="py-8 text-center text-lg font-medium">No Logs Yet</div>
      ) : (
        combinedLogs.map((log, index) => {
          const key = 'id' in log ? log.id : `audit-${index.toString()}`;
          return <div key={key as string}>{renderAuditLogs(log)}</div>;
        })
      )}
    </div>
  );
});

const CommitteeDetails = memo(function CommitteeDetails({
  committee
}: {
  committee: GetCommitteesData['committees'][number];
}) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  return (
    <div className="flex h-full w-full flex-row">
      <div className="flex-1 pt-5 pr-3 pl-14">
        <CommitteeDetailsMeta committee={committee} />
        <div className="mt-7">
          <MembersSection committee={committee} />
        </div>
      </div>
      <div className="relative flex h-full w-8 items-center justify-center">
        <div className="h-full w-px bg-[#E5E5E5]" />
        <Button
          className="absolute top-1/2 z-10 h-8 w-8 -translate-y-1/2 rounded-full border border-[#E5E5E5] bg-white p-0 shadow-sm transition-opacity duration-200"
          onClick={() => {
            setIsSidebarOpen(!isSidebarOpen);
          }}
          size="icon"
          variant="ghost"
        >
          {isSidebarOpen ? (
            <PanelRightClose className="h-4 w-4 text-gray-600" />
          ) : (
            <PanelRightOpen className="h-4 w-4 text-gray-600" />
          )}
        </Button>
      </div>
      <div
        className={cn(
          'h-full overflow-hidden transition-all duration-500 ease-in-out',
          isSidebarOpen ? 'w-[28rem]' : 'w-0'
        )}
      >
        <div className="h-full w-[28rem] overflow-y-auto px-2.5 py-5">
          <div className="text-lg font-semibold">Audit Area</div>
          <Separator className="my-5" />
          <AuditSection
            auditLogs={committee.auditLogs}
            memberAuditLogs={committee.memberAuditLogs}
          />
        </div>
      </div>
    </div>
  );
});

const FilterBar = memo(function FilterBar({
  handleBreadcrumbReset,
  onCreateCommittee,
  searchTerm,
  selectedCommittee,
  setSearchTerm
}: {
  handleBreadcrumbReset: () => void;
  onCreateCommittee: () => void;
  searchTerm: string;
  selectedCommittee: GetCommitteesData['committees'][number] | null;
  setSearchTerm: (value: string) => void;
}) {
  const { role } = useOdooAuth();
  const isAdmin = role === 'admin';

  return (
    <div className="flex justify-between gap-x-2.5 border-b bg-[#FAFAFA] py-1.5 pr-5 pl-14 dark:bg-[#171717]">
      <div className="flex items-end gap-x-2.5">
        {selectedCommittee ? (
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink
                  className="cursor-pointer"
                  onClick={handleBreadcrumbReset}
                >
                  Committees
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{selectedCommittee.name}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        ) : (
          <div className="w-[300px]">
            <Field>
              <FieldLabel htmlFor="search-field">Search</FieldLabel>
              <Input
                id="search-field"
                onChange={e => {
                  setSearchTerm(e.target.value);
                }}
                placeholder="Search committees..."
                value={searchTerm}
              />
            </Field>
          </div>
        )}
      </div>
      <div className="flex items-end gap-x-2.5">
        {isAdmin && !selectedCommittee && (
          <Button onClick={onCreateCommittee}>
            <Plus />
            Create Committee
          </Button>
        )}
      </div>
    </div>
  );
});

const Committees = () => {
  const [selectedCommittee, setSelectedCommittee] = useState<string>();
  const [searchTerm, setSearchTerm] = useState('');
  const [createCommitteeOpen, setCreateCommitteeOpen] = useState(false);

  const {
    data,
    error: error,
    loading
  } = useQuery<GetCommitteesData>(GET_COMMITTEES);
  const committees = useMemo(() => data?.committees ?? [], [data?.committees]);
  const committeeData = useMemo(() => {
    return (
      committees.find(committee => committee.id === selectedCommittee) ?? null
    );
  }, [committees, selectedCommittee]);

  const handleExpand = useCallback(
    (committee: GetCommitteesData['committees'][number]) => {
      setSelectedCommittee(committee.id);
    },
    []
  );

  const handleBreadcrumbReset = useCallback(() => {
    setSelectedCommittee(undefined);
  }, []);

  const filteredCommittees = committees.filter(committee => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const matchesName = committee.name.toLowerCase().includes(searchLower);
    const matchesFund =
      committee.fundId?.name.toLowerCase().includes(searchLower) ?? false;
    const matchesDescription =
      committee.description?.toLowerCase().includes(searchLower) ?? false;
    return matchesName || matchesFund || matchesDescription;
  });

  return (
    <div className="flex h-full w-full flex-col">
      <FilterBar
        handleBreadcrumbReset={handleBreadcrumbReset}
        onCreateCommittee={() => {
          setCreateCommitteeOpen(true);
        }}
        searchTerm={searchTerm}
        selectedCommittee={committeeData}
        setSearchTerm={setSearchTerm}
      />
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="grid h-full place-content-center">
            <Spinner className="size-8 text-blue-500" />
          </div>
        ) : error ? (
          <div className="px-14 py-6">
            <div className="rounded-lg border border-red-200 bg-red-50 p-6">
              <h3 className="font-semibold text-red-800">
                Error loading committees
              </h3>
              <p className="mt-2 text-sm text-red-700">
                {'Failed to load committees. Please try again.'}
              </p>
            </div>
          </div>
        ) : committeeData ? (
          <CommitteeDetails committee={committeeData} />
        ) : (
          <CommitteesTable
            committees={filteredCommittees}
            onExpand={handleExpand}
            searchTerm={searchTerm}
          />
        )}
      </div>
      <CreateCommitteeDialog
        onOpenChange={setCreateCommitteeOpen}
        open={createCommitteeOpen}
      />
    </div>
  );
};

export default Committees;
